import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import {
  AdminSession,
  getAdminSessions,
  revokeAdminSession,
} from "src/utils/api/adminSessions";

type Props = { open: boolean; onClose: () => void };

export const SessionsDialog = ({ open, onClose }: Props) => {
  const [sessions, setSessions] = useState<AdminSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setSessions((await getAdminSessions()).data);
    } catch {
      setError("Unable to load sessions. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const revoke = async (id: string) => {
    setLoading(true);
    setError("");
    try {
      await revokeAdminSession(id);
      await load();
    } catch {
      setError("Unable to sign out that session. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Login sessions</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Multiple devices are allowed. Review your recent sessions and sign out
          devices you do not recognise. History is kept for 90 days; the latest
          100 sessions are shown.
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Browser / device</TableCell>
              <TableCell>Signed in</TableCell>
              <TableCell>Last activity</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.map((session) => (
              <TableRow key={session.id}>
                <TableCell sx={{ maxWidth: 280, overflowWrap: "anywhere" }}>
                  {session.current
                    ? "This browser"
                    : session.userAgent || "Unknown browser"}
                </TableCell>
                <TableCell>
                  {new Date(session.createdAt).toLocaleString()}
                </TableCell>
                <TableCell>
                  {new Date(session.lastActivityAt).toLocaleString()}
                </TableCell>
                <TableCell>
                  {session.active
                    ? "Active"
                    : session.revokedAt
                    ? "Signed out"
                    : "Expired"}
                </TableCell>
                <TableCell>
                  {session.active && !session.current && (
                    <Button
                      disabled={loading}
                      onClick={() => void revoke(session.id)}
                    >
                      Sign out
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!sessions.length && (
          <Typography sx={{ mt: 2 }}>
            {loading ? "Loading sessions…" : "No sessions found."}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => void load()} disabled={loading}>
          Refresh
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
